import { AxiosError } from 'axios';
import { useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import ModalViewJSON from '../../../../components/ModalViewJSON';
import { backendErrorNotification } from '../../../../components/Notification';
import HttpStatus from '../../../../enums/httpStatus';
import { PreviewIcon } from '../../../../icons';
import { backgroundProcessesApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';
import { JobHistory } from '../../../../types/apiResponse/jobs';

const BackgroundProcessesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [updateTable] = useState(0);
  const [jobHistories, setJobHistories] = useState<JobHistory[]>([]);
  const [dataToViewInJson, setDataToViewInJson] = useState('');

  const getItems: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = backgroundProcessesApi.axios.CancelToken.source();

    try {
      backgroundProcessesApi.query =
        `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await backgroundProcessesApi.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<JobHistory[]> = res.data;

      if (!data || data.length === 0) {
        hasMore = false;
      }

      return {
        data: data ?? [],
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!backgroundProcessesApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    } finally {
      backgroundProcessesApi.query = '';
    }
  };

  const handleStatusColumn = (row: JobHistory) => {
    switch (row.status) {
      case 'error':
        return <p style={{ color: theme.colors.negative }}>{t('error')}</p>;
      case 'success':
        return <p style={{ color: theme.colors.positive }}>{t('success')}</p>;
      case 'processing':
        return <p style={{ color: theme.colors.warning }}>{t('processing')}</p>;
      case 'inQueue':
        return <p>{t('inQueue')}</p>;
      default:
        return <p>{t('unknownStatus')}</p>;
    }
  };

  const handleDataColumn = (row: JobHistory) => (
    <PreviewIcon
      title={t('toView')}
      style={{ width: '100%' }}
      onClick={() => setDataToViewInJson(JSON.stringify(row.data, null, 2))}
    />
  );

  const handlePercentColumn = (row: JobHistory) => (
    <div style={{ maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
      <div style={{ maxWidth: '20px', cursor: 'default' }} title={`${row.percent}%`}>
        <CircularProgressbar
          value={row.percent}
          text={`${row.percent}%`}
          styles={{
            text: {
              fill: theme.colors.positive,
              fontSize: '25px',
            },
            path: {
              stroke: theme.colors.positive,
            },
          }}
        />
      </div>
    </div>
  );

  const handleExceptionColumn = (row: JobHistory) => {
    if (row.exception) {
      return (
        <PreviewIcon
          title={t('toView')}
          style={{ width: '100%' }}
          onClick={() => setDataToViewInJson(row.exception)}
        />
      );
    }

    return undefined;
  };

  const columns: Column<JobHistory>[] = [
    {
      label: t('job') as string,
      accessor: 'job',
    },
    {
      label: t('status') as string,
      Cell: handleStatusColumn,
    },
    {
      label: t('percent') as string,
      Cell: handlePercentColumn,
      cellStyle: { textAlign: 'center' },
      headerCellStyle: { textAlign: 'center' },
    },
    {
      label: t('data') as string,
      Cell: handleDataColumn,
      headerCellStyle: { textAlign: 'center' },
    },
    {
      label: t('exception') as string,
      Cell: handleExceptionColumn,
      headerCellStyle: { textAlign: 'center' },
    },
    {
      label: t('createdAt') as string,
      accessor: 'created_at',
    },
  ];

  return (
    <ContentContainer title={t('backgroundProcesses')}>
      {dataToViewInJson.length >= 1 && (
        <ModalViewJSON
          showModal={dataToViewInJson.length >= 1}
          closeModal={() => setDataToViewInJson('')}
          data={dataToViewInJson}
          title={t('data')}
        />
      )}

      <AsyncTable
        tableName={t('backgroundProcesses')}
        columns={columns}
        value={jobHistories}
        onChange={setJobHistories}
        requestPage={getItems}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />
    </ContentContainer>
  );
};

export default BackgroundProcessesPage;
